import { PURGE } from "redux-persist";

const initialState = {
  // Workflows list
  allWorkflows: [],
  workflowsLoading: false,
  workflowsError: null,
  workflowsTotal: 0,
  workflowsPage: 1,
  workflowsLimit: 20,

  // Single workflow
  singleWorkflow: {},
  singleWorkflowLoading: false,

  // Workflow executions
  executions: [],
  executionsLoading: false,
  executionsTotal: 0,

  // Execution details
  executionDetails: {},
  executionDetailsLoading: false,

  // Templates
  allWorkflowTemplates: [],
  templatesLoading: false,
  templates: null, // For system templates
  requiredConnections: null,

  // Versions
  workflowVersions: [],
  versionsLoading: false,
  versionComparison: null,

  // Scheduling
  workflowSchedules: [],
  schedulesLoading: false,

  // Analytics
  workflowAnalytics: null,
  workspaceAnalytics: null,
  analyticsLoading: false,

  // LangGraph
  langGraphNodeTypes: [],
  langGraphNodeMetadata: {},
  systemMetrics: null,
  langGraphLoading: false,
  
  // Execution Management
  checkpoints: [],
  currentExecution: null,
  executionStream: null,

  // CRM data
  contactTags: null,
  contactTagsLoading: false,
  contactTagsError: null,

  // Filters
  filters: {
    status: '',
    type: '',
    search: '',
    sortBy: 'updatedAt',
    sortOrder: 'desc',
  },

  // Builder state
  selectedNode: null,
  workflowNodes: [],
  workflowEdges: [],
  
  // UI state
  workflowBuilderModal: false,
  workflowExecutionModal: false,
  
  // Operation states
  isLoading: false,
  error: null,
};

const workflows = (state = initialState, { type, payload }) => {
  switch (type) {
    case PURGE:
      return initialState;

    // Get workflows
    case 'GET_ALL_WORKFLOWS':
      return {
        ...state,
        workflowsLoading: true,
        workflowsError: null,
      };

    case 'SET_ALL_WORKFLOWS':
      return {
        ...state,
        allWorkflows: payload.workflows || payload,
        workflowsTotal: payload.total || payload.length || 0,
        workflowsPage: payload.page || 1,
        workflowsLimit: payload.limit || 20,
        workflowsLoading: false,
        workflowsError: null,
      };

    // Single workflow
    case 'GET_SINGLE_WORKFLOW':
      return {
        ...state,
        singleWorkflowLoading: true,
      };

    case 'SET_SINGLE_WORKFLOW':
      return {
        ...state,
        singleWorkflow: payload,
        singleWorkflowLoading: false,
      };

    // Create/Update/Delete
    case 'CREATE_WORKFLOW':
    case 'UPDATE_WORKFLOW':
    case 'DELETE_WORKFLOW':
    case 'BATCH_DELETE_WORKFLOWS':
    case 'CLONE_WORKFLOW':
    case 'EXECUTE_WORKFLOW':
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case 'CREATE_WORKFLOW_SUCCESS':
      return {
        ...state,
        allWorkflows: [payload, ...state.allWorkflows],
        isLoading: false,
        error: null,
        workflowBuilderModal: false,
      };

    case 'UPDATE_WORKFLOW_SUCCESS':
      return {
        ...state,
        allWorkflows: state.allWorkflows.map(w =>
          w._id === payload._id ? payload : w
        ),
        singleWorkflow: state.singleWorkflow._id === payload._id ? payload : state.singleWorkflow,
        isLoading: false,
        error: null,
      };

    case 'UPDATE_WORKFLOW_IN_LIST':
      return {
        ...state,
        allWorkflows: state.allWorkflows.map(w =>
          w._id === payload._id ? payload : w
        ),
      };

    case 'DELETE_WORKFLOW_SUCCESS':
      return {
        ...state,
        allWorkflows: state.allWorkflows.filter(w => w._id !== payload),
        singleWorkflow: state.singleWorkflow._id === payload ? {} : state.singleWorkflow,
        isLoading: false,
        error: null,
      };

    case 'BATCH_DELETE_WORKFLOWS_SUCCESS':
      return {
        ...state,
        allWorkflows: state.allWorkflows.filter(w => !payload.includes(w._id)),
        singleWorkflow: payload.includes(state.singleWorkflow._id) ? {} : state.singleWorkflow,
        isLoading: false,
        error: null,
      };

    case 'CLONE_WORKFLOW_SUCCESS':
      return {
        ...state,
        allWorkflows: [payload, ...state.allWorkflows],
        isLoading: false,
        workflowsLoading: false,
        error: null,
      };

    case 'CLONE_WORKFLOW_FAIL':
      return {
        ...state,
        isLoading: false,
        workflowsLoading: false,
        error: payload,
      };

    case 'EXECUTE_WORKFLOW_SUCCESS':
      return {
        ...state,
        error: null,
        isLoading: false,
        workflowExecutionModal: false,
      };

    // Workflow executions
    case 'GET_WORKFLOW_EXECUTIONS':
      return {
        ...state,
        executionsLoading: true,
      };

    case 'SET_WORKFLOW_EXECUTIONS':
      const newExecutions = payload.append
        ? [...state.executions, ...(payload.executions || [])]
        : (payload.executions || payload);
      const total = payload.pagination?.total || payload.total || (payload.executions || payload).length || 0;

      return {
        ...state,
        executions: newExecutions,
        executionsTotal: total,
        executionsLoading: false,
      };

    // Execution details
    case 'GET_EXECUTION_DETAILS':
      return {
        ...state,
        executionDetailsLoading: true,
      };
    case 'SET_EXECUTION_DETAILS':
      return {
        ...state,
        executionDetails: payload,
        executionDetailsLoading: false,
      };

    // Templates
    case 'GET_ALL_WORKFLOW_TEMPLATES':
      return {
        ...state,
        templatesLoading: true,
      };

    case 'SET_ALL_WORKFLOW_TEMPLATES':
      return {
        ...state,
        allWorkflowTemplates: payload,
        templatesLoading: false,
      };

    // UI State
    case 'SET_WORKFLOW_FILTERS':
      return {
        ...state,
        filters: {
          ...state.filters,
          ...payload,
        },
      };

    case 'SET_SELECTED_NODE':
      return {
        ...state,
        selectedNode: payload,
      };

    case 'SET_SELECTED_WORKFLOW':
      return {
        ...state,
        singleWorkflow: payload,
      };

    case 'SET_WORKFLOW_NODES':
      return {
        ...state,
        workflowNodes: payload,
      };

    case 'SET_WORKFLOW_EDGES':
      return {
        ...state,
        workflowEdges: payload,
      };

    case 'SET_WORKFLOW_BUILDER_MODAL':
      return {
        ...state,
        workflowBuilderModal: payload,
      };

    case 'SET_WORKFLOW_EXECUTION_MODAL':
      return {
        ...state,
        workflowExecutionModal: payload,
      };

    // Version Management
    case 'GET_WORKFLOW_VERSIONS':
      return {
        ...state,
        versionsLoading: true,
      };

    case 'SET_WORKFLOW_VERSIONS':
      return {
        ...state,
        workflowVersions: payload,
        versionsLoading: false,
      };

    case 'CREATE_WORKFLOW_VERSION':
    case 'RESTORE_WORKFLOW_VERSION':
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case 'CREATE_WORKFLOW_VERSION_SUCCESS':
      return {
        ...state,
        workflowVersions: [payload, ...state.workflowVersions],
        isLoading: false,
        error: null,
      };

    case 'RESTORE_WORKFLOW_VERSION_SUCCESS':
      return {
        ...state,
        singleWorkflow: payload,
        isLoading: false,
        error: null,
      };

    case 'SET_VERSION_COMPARISON':
      return {
        ...state,
        versionComparison: payload,
      };

    // Scheduling
    case 'GET_WORKFLOW_SCHEDULES':
      return {
        ...state,
        schedulesLoading: true,
      };

    case 'SET_WORKFLOW_SCHEDULES':
      return {
        ...state,
        workflowSchedules: payload,
        schedulesLoading: false,
      };

    case 'CREATE_WORKFLOW_SCHEDULE':
    case 'UPDATE_WORKFLOW_SCHEDULE':
    case 'DELETE_WORKFLOW_SCHEDULE':
    case 'PAUSE_WORKFLOW_SCHEDULE':
    case 'RESUME_WORKFLOW_SCHEDULE':
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    // Analytics
    case 'GET_WORKFLOW_ANALYTICS':
    case 'GET_WORKSPACE_ANALYTICS':
      return {
        ...state,
        analyticsLoading: true,
      };

    case 'SET_WORKFLOW_ANALYTICS':
      return {
        ...state,
        workflowAnalytics: payload,
        analyticsLoading: false,
      };

    case 'SET_WORKSPACE_ANALYTICS':
      return {
        ...state,
        workspaceAnalytics: payload,
        analyticsLoading: false,
      };

    // Error handling
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: true,
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: payload,
        isLoading: false,
        workflowsLoading: false,
        singleWorkflowLoading: false,
        executionsLoading: false,
        executionDetailsLoading: false,
        templatesLoading: false,
      };

    // LangGraph Actions
    case 'GET_LANGGRAPH_NODE_TYPES':
    case 'GET_LANGGRAPH_NODE_METADATA':
    case 'GET_LANGGRAPH_SYSTEM_METRICS':
      return {
        ...state,
        langGraphLoading: true,
      };

    case 'SET_LANGGRAPH_NODE_TYPES':
      return {
        ...state,
        langGraphNodeTypes: payload,
        langGraphLoading: false,
      };

    case 'SET_LANGGRAPH_NODE_METADATA':
      return {
        ...state,
        langGraphNodeMetadata: payload,
        langGraphLoading: false,
      };

    case 'SET_SYSTEM_METRICS':
      return {
        ...state,
        systemMetrics: payload,
        langGraphLoading: false,
      };
    
    // Execution Management
    case 'SET_CHECKPOINTS':
      return {
        ...state,
        checkpoints: payload,
      };
    
    case 'SET_CURRENT_EXECUTION':
      return {
        ...state,
        currentExecution: payload,
      };
    
    case 'SET_EXECUTION_STREAM':
      return {
        ...state,
        executionStream: payload,
      };

    // Template-related cases
    case 'SET_SYSTEM_TEMPLATES':
      return {
        ...state,
        templates: payload,
        templatesLoading: false,
        loading: false,
      };
    
    case 'SET_REQUIRED_CONNECTIONS':
      return {
        ...state,
        requiredConnections: payload,
      };

    case 'VALIDATE_LANGGRAPH_NODE':
    case 'EXECUTE_LANGGRAPH_WORKFLOW':
    case 'TEST_LANGGRAPH_WORKFLOW':
    case 'VALIDATE_LANGGRAPH_WORKFLOW':
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case 'SET_CURRENT_EXECUTION':
      return {
        ...state,
        executionDetails: payload,
        isLoading: false,
      };

    // Get contact tags
    case 'GET_CONTACT_TAGS':
      return {
        ...state,
        contactTagsLoading: true,
        contactTagsError: null,
      };

    case 'SET_CONTACT_TAGS':
      console.log('[Reducer] SET_CONTACT_TAGS - payload:', payload);
      console.log('[Reducer] SET_CONTACT_TAGS - payload.tags:', payload?.tags);
      return {
        ...state,
        contactTags: payload,
        contactTagsLoading: false,
        contactTagsError: null,
      };

    case 'CLEAR_CONTACT_TAGS':
      return {
        ...state,
        contactTags: null,
        contactTagsLoading: false,
        contactTagsError: null,
      };

    case 'SET_CONTACT_TAGS_ERROR':
      return {
        ...state,
        contactTagsLoading: false,
        contactTagsError: payload,
      };

    case 'LOGOUT':
      return initialState;

    default:
      return state;
  }
};

export default workflows;